/**
 * What an invite that could not be emailed looks like, and the dialog that
 * hands it over.
 *
 * Its own file because **two screens send invites** — the Admin users list
 * and an admin's own record page — and a dialog that lived in one of them
 * would mean the other imported a page module to get at it, or drew a second
 * one that drifted.
 */
import { useState } from 'react'
import { Button } from '../../components/primitives.js'
import { Modal } from '../../components/overlays.js'
import { IconCheck, IconCopy, IconWarn } from '../../frame/icons.js'

/**
 * What `/admins/invite` and `/admins/:id/resend-invite` answer.
 *
 * `link` comes back only when the email did not carry it — once a mailbox is
 * connected the link is in the mailbox, and a second copy in a response
 * nothing displays would only widen where a live credential exists.
 */
export interface Delivered {
  email?: string
  delivered: boolean
  reason?: string
  link?: string
}

/**
 * The invite exists; the email did not go.
 *
 * Shown instead of the "Invite sent" toast, because a toast is for something
 * that worked and this is a handover: the account is made, the link is live,
 * and the one thing still to happen is a person sending it. The address it
 * was meant to reach is on screen beside it, so the message can be addressed
 * without going back to the list.
 *
 * The link is the invite. It is shown to the owner who just asked for it,
 * behind the step-up they just passed, and the row that it was shown rather
 * than emailed is in the audit log. What it replaces is worse: the link used
 * to be written to the server's log, where it lives as long as the log does
 * and is readable by everyone who can read it.
 */
export function InviteLink({ answer, onClose, kind = 'invite', lasts = '3 days' }: {
  /** Only ever rendered with a `link`; see the branch in `invite`. */
  answer: Delivered & { link: string }
  onClose: () => void
  /**
   * A client's people get two kinds of link from the console (step 27): an
   * invite for somebody who never set a password, and a reset for somebody
   * who did. Same handover, different words, so neither reads as the other.
   */
  kind?: 'invite' | 'password_reset'
  /** How long it works — an admin invite is three days, a client's seven. */
  lasts?: string
}) {
  const [copied, setCopied] = useState(false)
  const reset = kind === 'password_reset'
  return (
    <Modal
      open
      onClose={onClose}
      title={reset
        ? 'The reset link is ready, but the email did not send'
        : 'The invite is ready, but the email did not send'}
      width={520}
      footerNote={reset
        ? 'Shown once here. Sending another reset makes a new link.'
        : 'Shown once here. Resend invite on the row makes a new link.'}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      <div className="flex flex-col gap-3.5">
        <p className="m-0 text-cell leading-[1.45] text-text-body">
          <b className="font-semibold text-text">{answer.email}</b>
          {reset ? ' can choose a new password.' : ' has an account waiting.'}
          {` Send them this link yourself — it works once and expires in ${lasts}.`}
        </p>

        <div className="flex flex-col gap-2 rounded-field bg-surface-alt p-3">
          {/*
            * A read-only box rather than bare text: a link this long wraps,
            * and selecting a wrapped link by hand drops characters off the
            * end without saying so.
            */}
          <input
            readOnly
            value={answer.link}
            aria-label={reset ? 'The reset link' : 'The invite link'}
            onFocus={(event) => event.currentTarget.select()}
            className="w-full rounded-field border border-line bg-surface px-2.5 py-2
                       font-mono text-[11.5px] text-text"
          />
          <Button
            onClick={() => {
              void navigator.clipboard?.writeText(answer.link).catch(() => {})
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
          >
            {copied ? <IconCheck size={15} /> : <IconCopy size={15} />}
            {copied ? 'Copied' : 'Copy the link'}
          </Button>
        </div>

        {answer.reason && (
          <p className="m-0 flex items-start gap-2 rounded-field bg-warn-bg px-3 py-2.5
                        text-[11.5px] leading-[1.45] text-warn">
            <span className="mt-px shrink-0"><IconWarn size={14} /></span>
            <span>{answer.reason}</span>
          </p>
        )}
      </div>
    </Modal>
  )
}
